import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePagesTranslation } from "../locales";
import LandingJigsawPlayPreview from "./LandingJigsawPlayPreview";
import LandingSlidingPlayPreview from "./LandingSlidingPlayPreview";
import LandingWordWheelPlayPreview from "./LandingWordWheelPlayPreview";
import {
    fetchJigsawHeroShot,
    fetchSlidingHeroShot,
    fetchWordWheelHeroShot,
    FALLBACK_HERO_SHOTS,
} from "./landingHeroShots";

/**
 * Drop a gameplay clip in with REACT_APP_LANDING_HERO_VIDEO_URL (mp4/webm/gif).
 * Until one exists, the frame rotates published Jigsaw, Sliding, and Word Wheel shots.
 */
const HERO_VIDEO_URL = process.env.REACT_APP_LANDING_HERO_VIDEO_URL || "";

const SHOT_INTERVAL_MS = 3200;
const BETWEEN_PLAY_PAUSE_MS = 5000;

function isPlayShotKind(kind) {
    return kind === "jigsaw-play" || kind === "sliding-play" || kind === "word-wheel-play";
}

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function ChevronLeftIcon() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
                fill="currentColor"
                d="M14.7 5.3a1 1 0 0 1 0 1.4L9.4 12l5.3 5.3a1 1 0 1 1-1.4 1.4l-6-6a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 1.4 0z"
            />
        </svg>
    );
}

function ChevronRightIcon() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
                fill="currentColor"
                d="M9.3 5.3a1 1 0 0 1 1.4 0l6 6a1 1 0 0 1 0 1.4l-6 6a1 1 0 1 1-1.4-1.4L14.6 12 9.3 6.7a1 1 0 0 1 0-1.4z"
            />
        </svg>
    );
}

/** 320×520 phone frame showing a gameplay preview. */
export default function LandingHeroPhoneMockup({ mediaAlt, contentLanguage }) {
    const { t } = usePagesTranslation();
    const isVideo = HERO_VIDEO_URL && !HERO_VIDEO_URL.toLowerCase().endsWith(".gif");
    const isGif = HERO_VIDEO_URL && HERO_VIDEO_URL.toLowerCase().endsWith(".gif");
    const [shots, setShots] = useState(FALLBACK_HERO_SHOTS);
    const [shotIndex, setShotIndex] = useState(0);
    const [resumeAt, setResumeAt] = useState(0);
    const [playBusy, setPlayBusy] = useState(false);
    const betweenPlayTimerRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        const applyShot = (next) => {
            if (cancelled || !next?.id) return;
            setShots((prev) => prev.map((shot) => (shot.id === next.id ? { ...shot, ...next } : shot)));
        };
        fetchJigsawHeroShot(contentLanguage).then(applyShot);
        fetchSlidingHeroShot(contentLanguage).then(applyShot);
        fetchWordWheelHeroShot().then(applyShot);
        return () => {
            cancelled = true;
        };
    }, [contentLanguage]);

    useEffect(() => () => {
        if (betweenPlayTimerRef.current) {
            window.clearTimeout(betweenPlayTimerRef.current);
        }
    }, []);

    const clearBetweenPlayTimer = useCallback(() => {
        if (betweenPlayTimerRef.current) {
            window.clearTimeout(betweenPlayTimerRef.current);
            betweenPlayTimerRef.current = 0;
        }
    }, []);

    useEffect(() => {
        if (HERO_VIDEO_URL || prefersReducedMotion() || shots.length < 2) return undefined;
        if (isPlayShotKind(shots[shotIndex]?.kind)) {
            return undefined;
        }
        if (playBusy) return undefined;
        const timer = setInterval(
            () => setShotIndex((i) => (i + 1) % shots.length),
            SHOT_INTERVAL_MS,
        );
        return () => clearInterval(timer);
    }, [shots, resumeAt, shotIndex, playBusy]);

    const goBy = useCallback((step) => {
        if (shots.length < 2) return;
        clearBetweenPlayTimer();
        setShotIndex((i) => (i + step + shots.length) % shots.length);
        setResumeAt((n) => n + 1);
    }, [shots.length, clearBetweenPlayTimer]);

    const onPlayComplete = useCallback(() => {
        if (shots.length < 2) return;
        clearBetweenPlayTimer();
        betweenPlayTimerRef.current = window.setTimeout(() => {
            betweenPlayTimerRef.current = 0;
            setShotIndex((i) => (i + 1) % shots.length);
            setResumeAt((n) => n + 1);
        }, BETWEEN_PLAY_PAUSE_MS);
    }, [shots.length, clearBetweenPlayTimer]);

    const active = shots[shotIndex] || shots[0];
    const liveAlt = active?.name
        ? `${active.name} on a phone`
        : mediaAlt;
    const showNav = !isVideo && !isGif && shots.length > 1;

    return (
        <div className="pi-phone-wrap">
            <div className="pi-phone">
                <div className="pi-phone__notch" aria-hidden />
                <div className="pi-phone__screen">
                    {isVideo ? (
                        <video
                            className="pi-phone__media pi-phone__media--active"
                            src={HERO_VIDEO_URL}
                            poster={shots[0]?.src}
                            autoPlay
                            muted
                            loop
                            playsInline
                            aria-label={liveAlt}
                        />
                    ) : isGif ? (
                        <img
                            className="pi-phone__media pi-phone__media--active"
                            src={HERO_VIDEO_URL}
                            alt={liveAlt}
                        />
                    ) : (
                        shots.map((shot, i) => {
                            const active = i === shotIndex;
                            if (shot.kind === "jigsaw-play") {
                                return (
                                    <div
                                        key={shot.id}
                                        className={`pi-phone__play${active ? " pi-phone__play--active" : ""}`}
                                        aria-hidden={active ? undefined : true}
                                    >
                                        <LandingJigsawPlayPreview
                                            imageUrl={shot.imageUrl || shot.src}
                                            grid={shot.grid}
                                            paths={shot.paths}
                                            boardPieceIndices={shot.boardPieceIndices}
                                            trayPieceIndices={shot.trayPieceIndices}
                                            svgPathFallback={shot.svgPathFallback}
                                            active={active}
                                            onBusyChange={setPlayBusy}
                                            onPlayComplete={onPlayComplete}
                                        />
                                    </div>
                                );
                            }
                            if (shot.kind === "sliding-play") {
                                return (
                                    <div
                                        key={shot.id}
                                        className={`pi-phone__play${active ? " pi-phone__play--active" : ""}`}
                                        aria-hidden={active ? undefined : true}
                                    >
                                        <LandingSlidingPlayPreview
                                            imageUrl={shot.imageUrl || shot.src}
                                            gridSize={shot.gridSize}
                                            title={shot.title}
                                            active={active}
                                            onBusyChange={setPlayBusy}
                                            onPlayComplete={onPlayComplete}
                                        />
                                    </div>
                                );
                            }
                            if (shot.kind === "word-wheel-play") {
                                return (
                                    <div
                                        key={shot.id}
                                        className={`pi-phone__play${active ? " pi-phone__play--active" : ""}`}
                                        aria-hidden={active ? undefined : true}
                                    >
                                        <LandingWordWheelPlayPreview
                                            puzzle={shot.puzzle}
                                            foundWords={shot.foundWords}
                                            active={active}
                                            onBusyChange={setPlayBusy}
                                            onPlayComplete={onPlayComplete}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <img
                                    key={`${shot.id}-${shot.src}`}
                                    className={`pi-phone__media${active ? " pi-phone__media--active" : ""}`}
                                    src={shot.src}
                                    alt={active ? liveAlt : ""}
                                    aria-hidden={active ? undefined : true}
                                    decoding="async"
                                />
                            );
                        })
                    )}
                </div>
            </div>
            {showNav ? (
                <div className="pi-phone__nav">
                    <button
                        type="button"
                        className="pi-phone__nav-btn"
                        onClick={() => goBy(-1)}
                        aria-label={t("landingApp.hero-phone-prev", "Previous app screenshot")}
                    >
                        <ChevronLeftIcon />
                    </button>
                    <button
                        type="button"
                        className="pi-phone__nav-btn"
                        onClick={() => goBy(1)}
                        aria-label={t("landingApp.hero-phone-next", "Next app screenshot")}
                    >
                        <ChevronRightIcon />
                    </button>
                </div>
            ) : null}
        </div>
    );
}
